import { getLanguage, t } from "../../features/store-language/i18n.ts";
import { HttpService } from "../../utils/DesigningHttpService.js";
import { subscribeLanguage } from "../../features/store-language/i18n.ts";
import AppFeedback from "../shared/AppFeedback.tsx";
import { buildCategoryTree, type StoreCategoryNode } from "./Category/categoryTree.ts";
import { filterValuesFromQuery, groupStoreFilters, type StoreFilterGroup } from "./storefrontFilters.ts";

const http = new HttpService();
const tenantInfoPromises = new Map<string, Promise<any>>();
subscribeLanguage(function () { tenantInfoPromises.clear(); });

export function storeMediaReference(id?: number | null): any {
  return id ? `store:${id}` : null;
}

export async function readStore(path: string): Promise<any> {
  try {
    return await http.getResource(`/catalog/${path}`);
  } catch (error) {
    AppFeedback.httpError(error);
    throw error;
  }
}

export async function getTenantInfo({ notifyErrors = true, anonymous = false }: { notifyErrors?: boolean; anonymous?: boolean } = {}): Promise<any> {
  const cacheKey = `${getLanguage()}:${anonymous ? "anonymous" : "session"}`;
  let tenantInfoPromise = tenantInfoPromises.get(cacheKey);
  if (!tenantInfoPromise) {
    const request = anonymous
      ? http.getAnonymousResource("/catalog/tenant-info")
      : http.getResource("/catalog/tenant-info");
    tenantInfoPromise = request.then(function (tenantInfo) {
      return { ...tenantInfo, name: tenantInfo.title, fileId: storeMediaReference(tenantInfo.logoMediaId), logoFileId: storeMediaReference(tenantInfo.logoMediaId) };
    }).catch(function (error) {
      tenantInfoPromises.delete(cacheKey);
      throw error;
    });
    tenantInfoPromises.set(cacheKey, tenantInfoPromise);
  }
  try {
    return await tenantInfoPromise;
  } catch (error) {
    if (notifyErrors) AppFeedback.httpError(error);
    throw error;
  }
}

export async function getStoreCategories(): Promise<StoreCategoryNode[]> {
  const categories = await readStore("categories");
  return buildCategoryTree(categories.map(function (category) {
    return { ...category, name: category.title, fileId: storeMediaReference(category.imageMediaId) };
  }));
}

export async function getStoreFilters(slug?: string): Promise<StoreFilterGroup[]> {
  const values = await readStore(`filters?categorySlug=${encodeURIComponent(slug || "")}`);
  return groupStoreFilters(values);
}

export async function getStoreProducts(legacyQuery: URLSearchParams): Promise<any> {
  const query = new URLSearchParams({
    page: legacyQuery.get("page") || "1", pageSize: legacyQuery.get("pageSize") || "20",
    categorySlug: legacyQuery.get("slug") || "", search: legacyQuery.get("SearchTerm") || "",
    sort: legacyQuery.get("sortBy") || "default",
  });
  filterValuesFromQuery(legacyQuery).forEach(function (value) {
    query.append("valueIds", String(value));
  });
  const page = await readStore(`products?${query}`);
  return { ...page, data: page.items.map(function (product) {
    const palette = product.attributes.find(function (value) { return value.code === "palette"; });
    const productImage = storeMediaReference(product.thumbnailMediaId || product.mainImageMediaId);
    return { ...product, paletteTitle: palette?.title,
      fileId: productImage,
      schemaFileId: productImage,
      previewOptions: (product.previewOptions || []).map(function (option) {
        return { ...option, fileId: storeMediaReference(option.mediaId) };
      }),
      colorFiles: product.colors.map(function (color) {
        return { backgroundColorId: color.attributeValueId, backgroundColorTitle: color.title,
          backgroundColorHexCode: (color.hexCode || "808080").replace(/^#/, ""), fileId: storeMediaReference(color.mediaId) };
      }),
    };
  }) };
}

// Only reorder existing variant rows. Optional ranking never supplies product membership.
export function compareDimensionPriority(first: { displayPriority?: number }, second: { displayPriority?: number }): number {
  const a = first.displayPriority;
  const b = second.displayPriority;
  if (a === undefined) return b === undefined ? 0 : 1;
  if (b === undefined) return -1;
  return a - b;
}

async function getDimensionPriorities(masterProductId: number): Promise<Record<string, number>> {
  const controller = new AbortController();
  const timeout = setTimeout(function () { controller.abort(); }, 5000);
  try {
    const response: unknown = await http.getResource(`/catalog/products/${masterProductId}/dimension-priorities`, controller.signal);
    if (!response || typeof response !== "object" || Array.isArray(response)) return {};
    return Object.fromEntries(Object.entries(response).filter(function ([id, value]) {
      return /^[1-9]\d*$/.test(id) && typeof value === "number" && Number.isSafeInteger(value) && value > 0;
    }));
  } catch (error) {
    // Older Portal deployments do not expose this optional endpoint yet.
    const status = (error as { response?: { status?: number } }).response?.status;
    if (status !== 404) AppFeedback.httpError(error);
    return {};
  } finally {
    clearTimeout(timeout);
  }
}

export async function getStorePreview(
  title: string,
  masterId: string | null,
  _priority: URLSearchParams,
  categorySlug?: string,
  variantId?: string,
  productSlug?: string,
  variantTitle?: string,
): Promise<any> {
  let id = Number(masterId);
  if (!productSlug && (!Number.isSafeInteger(id) || id <= 0)) {
    const page = await readStore(`products?exactTitle=${encodeURIComponent(title)}&categorySlug=${encodeURIComponent(categorySlug || "")}&pageSize=100`);
    if (page.items.length !== 1) {
      throw new Error(t("این لینک قدیمی مبهم یا نامعتبر است؛ کالا را از فهرست محصولات انتخاب کنید."));
    }
    id = page.items[0].id;
  }
  const query = new URLSearchParams({ categorySlug: categorySlug || "" });
  if (variantId) { query.set("legacyVariantId", variantId); }
  const address = productSlug
    ? `product-pages/${encodeURIComponent(productSlug)}${variantTitle === undefined ? "" : `/${encodeURIComponent(variantTitle)}`}`
    : `products/${id}`;
  const detail = await readStore(`${address}?${query}`);
  id = detail.product.id;
  const palette = detail.product.attributes.find(function (value) { return value.code === "palette"; });
  const dimensionPriorities = await getDimensionPriorities(id);
  const groups = new Map<number, any>();
  const isVariantPage = variantId !== undefined || variantTitle !== undefined;
  const selectedVariant = variantTitle !== undefined ? detail.variants[0] : variantId === undefined ? null : detail.variants.find(function (variant) {
    return String(variant.id) === variantId;
  });
  const variants = !isVariantPage ? detail.variants : selectedVariant ? [selectedVariant] : [];
  variants.forEach(function (variant) {
    const color = variant.attributes.find(function (value) { return value.code === "color"; });
    const dimension = variant.attributes.find(function (value) { return value.code === "dimension"; });
    const image = variant.images[0];
    const key = color?.attributeValueId || 0;
    if (!groups.has(key)) {
      groups.set(key, { fileId: storeMediaReference(image?.mainImageMediaId), colorTitle: color?.title || t("سایر"),
        englishTitle: "", hexCode: (color?.hexCode || "808080").replace(/^#/, ""), dimensions: [] });
    }
    groups.get(key).dimensions.push({ patternId: variant.id, addressTitle: variant.addressTitle, code: variant.code, dimension: dimension?.title || variant.title,
      fileId: storeMediaReference(image?.mainImageMediaId), images: variant.images,
      availableTakhteStock: variant.quantity, isAvailable: variant.isAvailable,
      displayPriority: dimensionPriorities[String(variant.id)] });
  });
  groups.forEach(function (group) {
    group.dimensions.sort(compareDimensionPriority);
  });
  return { masterProductId: id, slug: detail.product.slug,
    variant: selectedVariant ? {
      id: selectedVariant.id, addressTitle: selectedVariant.addressTitle,
      title: selectedVariant.title, code: selectedVariant.code,
      attributes: [...detail.product.attributes, ...selectedVariant.attributes],
    } : null,
    name: detail.product.title, seoTitle: detail.product.seoTitle || detail.product.title,
    palette: palette?.title || "", paletteId: palette?.attributeValueId,
    colors: Array.from(groups.values()) };
}
